import type { ArmState } from './api'
import type { Site } from './types'

// clock is an arm time as a dispatcher reads it: the time today, with the day
// when it is not today. VMS sends it in the site's own zone; one whose offset
// differs from the viewer's is shown as the site's wall-clock time with its
// offset, since a bare time would be read as local (#248). Go writes a zero
// offset as Z, which is read too: a site in UTC otherwise showed the viewer's
// time with no label (#251).
export function clock(at: string): string {
  const m = /T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:([+-])(\d{2}):(\d{2})|Z)$/.exec(at)
  const d = new Date(at)
  if (m) {
    const [sign, hours, minutes] = m[3] ? [m[3], m[4], m[5]] : ['+', '00', '00']
    const siteOffset = (sign === '-' ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
    if (siteOffset !== -d.getTimezoneOffset()) {
      const mins = Number(minutes) ? `:${minutes}` : ''
      const label = siteOffset === 0 ? 'UTC' : `UTC${sign === '-' ? '−' : '+'}${Number(hours)}${mins}`
      const siteDay = new Date(Date.parse(at.slice(0, 10) + 'T12:00:00Z')).toLocaleDateString([], { weekday: 'short', timeZone: 'UTC' })
      const today = new Date(Date.now() + siteOffset * 60_000).toISOString().slice(0, 10)
      const time = `${m[1]}:${m[2]}`
      return `${at.slice(0, 10) === today ? '' : siteDay + ' '}${time} (${label})`
    }
  }
  return localClock(d)
}

// localClock is a moment in the viewer's own zone, with the day when not
// today. hourCycle h23, as hour12: false printed 24:05 on some runtimes.
function localClock(d: Date): string {
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  if (d.toDateString() === new Date().toDateString()) return time
  return `${d.toLocaleDateString([], { weekday: 'short' })} ${time}`
}

// armsNext is when a disarmed site next arms on schedule, if it does, so
// "Disarmed" is not read as for good (#236). It sits beside the switch, never
// inside it: there it squeezed out the site's name (#238).
export function armsNext(state: ArmState): string | null {
  const at = armsAt(state)
  return at && `Arms ${at}`
}

// armsAt is the time alone, for the alerts rail's "Alerts paused until 22:00".
export function armsAt(state: ArmState): string | null {
  if (state.armed || state.next?.action !== 'arm') return null
  return clock(state.next.at)
}

// armsAgain is when an armed site being disarmed would next arm. GET /arm
// names only the schedule's next change, and for an armed site that is usually
// a disarm, after which the time of the arm is not known here.
export function armsAgain(state: ArmState): string {
  if (state.next?.action === 'arm') return `It arms again at ${clock(state.next.at)}, on its schedule.`
  if (state.next) return 'It arms again when its schedule next arms it.'
  return 'It has no schedule, so it stays disarmed until someone arms it.'
}

export function armTitle(site: Site, state: ArmState): string {
  const name = site.name || site.id
  const action = state.armed ? `Disarm ${name}` : `Arm ${name}`
  const c = state.changed
  if (!c) return action
  const who = c.by === 'schedule' ? 'the schedule' : c.who || 'a dispatcher'
  // Local: VMS stamps a change by its own clock, not in the site's zone.
  return `${state.armed ? 'Armed' : 'Disarmed'} by ${who} at ${localClock(new Date(c.at))}. ${action}.`
}
